import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../PageLayout/Footer';

const MODES = ['login', 'register', 'forgot'];
const REGISTER_FLOW = [
  { key: 'form', label: 'Credentials' },
  { key: 'confirm', label: 'Confirm email' },
];
const FORGOT_FLOW = [
  { key: 'request', label: 'Request' },
  { key: 'verify', label: 'Verify' },
  { key: 'reset', label: 'Reset' },
];
const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'FR', label: 'France' },
  { value: 'SN', label: 'Senegal' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'BR', label: 'Brazil' },
  { value: 'AU', label: 'Australia' },
];
const SENSITIVE_KEYS = ['password', 'confirm_password', 'confirmpassword', 'new_password', 'newpassword'];

const sanitizePayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const clone = JSON.parse(JSON.stringify(payload));
  Object.keys(clone).forEach((key) => {
    if (SENSITIVE_KEYS.includes(key.toLowerCase())) {
      clone[key] = '••••••••';
    }
  });

  return clone;
};

const initialState = {
  email: '',
  password: '',
  confirmPassword: '',
  dateOfBirth: '',
  country: COUNTRIES[0]?.value || '',
  registerCode: '',
  forgotCode: '',
  newPassword: '',
  confirmNewPassword: '',
};

const endpointMap = {
  login: 'https://backproject.meanc.net/meanc/secure-auth/token/',
  register: 'https://backproject.meanc.net/meanc/secure-auth/register/',
  confirmRegistration: 'https://backproject.meanc.net/meanc/secure-auth/confirm/',
  forgotRequest: 'https://backproject.meanc.net/meanc/password/forgot/',
  forgotVerify: 'https://backproject.meanc.net/meanc/password/forgot/verify/',
  forgotReset: 'https://backproject.meanc.net/meanc/password/forgot/reset/',
  refresh: 'https://backproject.meanc.net/meanc/auth/token/refresh/',
  profile: 'https://backproject.meanc.net/meanc/users/me/profile-show/',
};

const AccessLabPage = () => {
  const [mode, setMode] = useState('login');
  const [registerStage, setRegisterStage] = useState('form');
  const [forgotStage, setForgotStage] = useState('request');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState({ type: 'neutral', message: '' });
  const [formState, setFormState] = useState(() => ({ ...initialState }));
  const [session, setSession] = useState(null);
  const [resetTicket, setResetTicket] = useState(null);
  const [lastExchange, setLastExchange] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setStatus({ type: 'neutral', message: '' });
    setFormState({ ...initialState });
    setRegisterStage('form');
    setForgotStage('request');
    setResetTicket(null);
  };

  const recordExchange = useCallback((endpoint, payload, statusCode, body, method = 'POST') => {
    setLastExchange({
      endpoint,
      payload,
      method,
      statusCode,
      body,
      at: new Date().toLocaleTimeString(),
    });
  }, []);

  const currentEndpoint = useMemo(() => {
    if (mode === 'login') {
      return endpointMap.login;
    }
    if (mode === 'register') {
      return registerStage === 'form' ? endpointMap.register : endpointMap.confirmRegistration;
    }
    if (forgotStage === 'request') {
      return endpointMap.forgotRequest;
    }
    if (forgotStage === 'verify') {
      return endpointMap.forgotVerify;
    }
    return endpointMap.forgotReset;
  }, [mode, registerStage, forgotStage]);

  const timeline = useMemo(() => {
    if (mode === 'register') {
      return { steps: REGISTER_FLOW, active: registerStage };
    }
    if (mode === 'forgot') {
      return { steps: FORGOT_FLOW, active: forgotStage };
    }
    return null;
  }, [mode, registerStage, forgotStage]);

  const formTitle = useMemo(() => {
    if (mode === 'login') {
      return 'Request access token';
    }
    if (mode === 'register') {
      return registerStage === 'form' ? 'Create secure profile' : 'Confirm your inbox';
    }
    if (forgotStage === 'request') {
      return 'Need a reset link?';
    }
    if (forgotStage === 'verify') {
      return 'Verify reset code';
    }
    return 'Set a new password';
  }, [mode, registerStage, forgotStage]);

  const submitLabel = useMemo(() => {
    if (mode === 'login') {
      return 'Issue token';
    }
    if (mode === 'register') {
      return registerStage === 'form' ? 'Create account' : 'Confirm account';
    }
    if (forgotStage === 'request') {
      return 'Send reset';
    }
    if (forgotStage === 'verify') {
      return 'Verify code';
    }
    return 'Update password';
  }, [mode, registerStage, forgotStage]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const fetchProfile = useCallback(
    async (accessToken, silent = false) => {
      if (!accessToken) {
        setStatus({ type: 'error', message: 'Access token missing. Log in first.' });
        return;
      }

      setProfileLoading(true);
      const authPrefix = accessToken.includes('.') ? 'Bearer' : 'Token';

      try {
        const response = await fetch(endpointMap.profile, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${authPrefix} ${accessToken}`,
          },
        });
        const data = await response.json().catch(() => ({}));
        recordExchange(endpointMap.profile, null, response.status, data, 'GET');

        if (!response.ok) {
          throw new Error(data.detail || data.error || 'Unable to fetch profile.');
        }

        setProfile(data);
        if (!silent) {
          setStatus({ type: 'success', message: 'Profile synced from /users/me/profile-show/' });
        }
      } catch (error) {
        setStatus({ type: 'error', message: error.message });
      } finally {
        setProfileLoading(false);
      }
    },
    [recordExchange]
  );

  useEffect(() => {
    if (session?.access) {
      localStorage.setItem('meanc_access', session.access);
      if (session.refresh) {
        localStorage.setItem('meanc_refresh', session.refresh);
      }
      fetchProfile(session.access, true);
      navigate('/app', { replace: true });
    }
  }, [session, fetchProfile, navigate]);

  const refreshAccessToken = async () => {
    if (!session?.refresh) {
      setStatus({ type: 'error', message: 'No refresh token available.' });
      return;
    }

    setIsRefreshing(true);

    try {
      const payload = { refresh: session.refresh };
      const response = await fetch(endpointMap.refresh, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      recordExchange(endpointMap.refresh, sanitizePayload(payload), response.status, data);

      if (!response.ok) {
        throw new Error(data.detail || data.error || 'Unable to refresh access token.');
      }

      const newAccess = data.access || data.token || data.access_token;
      if (!newAccess) {
        throw new Error('API did not provide a new access token.');
      }

      setSession((prev) => ({ ...prev, access: newAccess }));
      setStatus({ type: 'success', message: 'Access token refreshed.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setStatus({ type: 'neutral', message: 'Contacting endpoint…' });

    try {
      if (mode === 'login') {
        const payload = { email: formState.email, password: formState.password };
        const response = await fetch(endpointMap.login, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        recordExchange(endpointMap.login, sanitizePayload(payload), response.status, data);

        if (!response.ok) {
          throw new Error(data.detail || data.error || 'Unable to sign in with those credentials.');
        }

        const access = data.access || data.token || data.access_token;
        if (!access) {
          throw new Error('No access token returned by the API.');
        }

        setSession({
          access,
          refresh: data.refresh || data.refresh_token || null,
          issuedAt: Date.now(),
        });
        setStatus({ type: 'success', message: 'Token granted. Syncing profile…' });
      } else if (mode === 'register') {
        if (registerStage === 'form') {
          if (formState.password !== formState.confirmPassword) {
            throw new Error('Passwords must match before we can continue.');
          }
          const payload = {
            email: formState.email,
            password: formState.password,
            confirm_password: formState.confirmPassword,
            date_of_birth: formState.dateOfBirth,
            country: formState.country,
          };
          const response = await fetch(endpointMap.register, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          recordExchange(endpointMap.register, sanitizePayload(payload), response.status, data);

          if (!response.ok) {
            throw new Error(data.detail || data.error || data.message || 'Registration failed.');
          }

          setRegisterStage('confirm');
          setFormState((prev) => ({ ...prev, password: '', confirmPassword: '' }));
          setStatus({ type: 'success', message: data.message || 'Code sent. Confirm to activate the account.' });
        } else {
          const payload = { email: formState.email, code: formState.registerCode };
          const response = await fetch(endpointMap.confirmRegistration, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          recordExchange(endpointMap.confirmRegistration, payload, response.status, data);

          if (!response.ok) {
            throw new Error(data.detail || data.error || data.message || 'Confirmation failed.');
          }

          const email = formState.email;
          setRegisterStage('form');
          setForgotStage('request');
          setFormState({ ...initialState, email });
          setMode('login');
          setStatus({ type: 'success', message: 'Account confirmed. Use the login tab with the same email.' });
        }
      } else {
        if (forgotStage === 'request') {
          const payload = { email: formState.email };
          const response = await fetch(endpointMap.forgotRequest, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          recordExchange(endpointMap.forgotRequest, payload, response.status, data);

          if (!response.ok) {
            throw new Error(data.detail || data.error || data.message || 'Could not send reset link.');
          }

          setForgotStage('verify');
          setStatus({ type: 'success', message: data.message || 'Email sent. Enter the code once it arrives.' });
        } else if (forgotStage === 'verify') {
          const payload = { email: formState.email, code: formState.forgotCode };
          const response = await fetch(endpointMap.forgotVerify, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          recordExchange(endpointMap.forgotVerify, payload, response.status, data);

          if (!response.ok) {
            throw new Error(data.detail || data.error || data.message || 'Verification failed.');
          }

          setResetTicket(data.reset_token || data.token || data.ticket || null);
          setForgotStage('reset');
          setStatus({ type: 'success', message: data.message || 'Code verified. Choose a new password.' });
        } else {
          if (formState.newPassword !== formState.confirmNewPassword) {
            throw new Error('New password fields must match.');
          }
          const payload = {
            email: formState.email,
            new_password: formState.newPassword,
            confirm_password: formState.confirmNewPassword,
            reset_token: resetTicket,
            code: formState.forgotCode,
          };
          const response = await fetch(endpointMap.forgotReset, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          const data = await response.json().catch(() => ({}));
          recordExchange(endpointMap.forgotReset, sanitizePayload(payload), response.status, data);

          if (!response.ok) {
            throw new Error(data.detail || data.error || data.message || 'Reset failed.');
          }

          setForgotStage('request');
          setResetTicket(null);
          setFormState({ ...initialState, email: formState.email });
          setStatus({ type: 'success', message: data.message || 'Password updated. Use the login tab next.' });
        }
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const profileFields = useMemo(() => {
    if (!profile) {
      return [];
    }
    const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
    return [
      { label: 'Full name', value: fullName || profile.username },
      { label: 'Email', value: profile.email },
      { label: 'User type', value: profile.user_type || profile.role },
      { label: 'Country', value: profile.country },
      { label: 'Birthday', value: profile.date_of_birth },
    ].filter((item) => item.value);
  }, [profile]);

  const profileInitials = useMemo(() => {
    if (!profile) {
      return 'ME';
    }
    const primary = profile.first_name || profile.username || profile.email || 'M';
    const secondary = profile.last_name || '';
    return `${primary.charAt(0)}${secondary.charAt(0)}`.toUpperCase();
  }, [profile]);

  return (
    <div className="access-lab">
      <div className="access-lab__hero">
        <p>Live endpoint playground</p>
        <h1>Secure Access Console</h1>
        <p>Issue tokens, onboard new users, recover credentials, and pull live profile data without leaving this page.</p>
      </div>

      <div className="access-lab__content">
        <aside className="access-lab__Sidebar">
          <h3>Choose flow</h3>
          <div className="access-lab__tabs">
            {MODES.map((item) => (
              <button
                key={item}
                type="button"
                className={item === mode ? 'is-active' : ''}
                onClick={() => handleModeChange(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="access-lab__status">
            <p className={`access-lab__status-text ${status.type}`}>
              {status.message || 'Awaiting input…'}
            </p>
            <small>Connected to <code>backproject.meanc.net</code></small>
          </div>
          {session && (
            <div className="access-lab__session">
              <div>
                <p>Access token</p>
                <code>{session.access ? `${session.access.slice(0, 48)}…` : '—'}</code>
              </div>
              {session.refresh && (
                <div>
                  <p>Refresh token</p>
                  <code>{`${session.refresh.slice(0, 48)}…`}</code>
                </div>
              )}
              <div className="session-actions">
                <button type="button" onClick={refreshAccessToken} disabled={!session.refresh || isRefreshing}>
                  {isRefreshing ? 'Refreshing…' : 'Refresh token'}
                </button>
                <button
                  type="button"
                  onClick={() => fetchProfile(session.access)}
                  disabled={!session.access || profileLoading}
                >
                  {profileLoading ? 'Syncing…' : 'Sync profile'}
                </button>
              </div>
            </div>
          )}
        </aside>

        <div className="access-lab__main">
          <section className="access-lab__form">
            <div className="access-lab__formHeader">
              <h2>{formTitle}</h2>
              <p className="access-lab__endpoint">{currentEndpoint}</p>
            </div>
            {timeline && (
              <div className="access-lab__timeline">
                {timeline.steps.map((step) => (
                  <span
                    key={step.key}
                    className={`access-lab__chip ${timeline.active === step.key ? 'is-active' : ''}`}
                  >
                    {step.label}
                  </span>
                ))}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {(mode === 'login' || mode === 'register' || mode === 'forgot') && (
                <label>
                  Email
                  <input
                    type="email"
                    name="email"
                    required
                    value={formState.email}
                    onChange={handleChange}
                    placeholder="you@meanc.com"
                    readOnly={
                      (mode === 'register' && registerStage === 'confirm') ||
                      (mode === 'forgot' && forgotStage !== 'request')
                    }
                  />
                </label>
              )}

              {(mode === 'login' || (mode === 'register' && registerStage === 'form')) && (
                <label>
                  Password
                  <input
                    type="password"
                    name="password"
                    required
                    value={formState.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                  />
                </label>
              )}

              {mode === 'register' && registerStage === 'form' && (
                <>
                  <label>
                    Confirm password
                    <input
                      type="password"
                      name="confirmPassword"
                      required
                      value={formState.confirmPassword}
                      onChange={handleChange}
                      placeholder="Repeat password"
                    />
                  </label>
                  <label>
                    Date of birth
                    <input
                      type="date"
                      name="dateOfBirth"
                      required
                      value={formState.dateOfBirth}
                      onChange={handleChange}
                    />
                  </label>
                  <label>
                    Country
                    <select name="country" value={formState.country} onChange={handleChange}>
                      {COUNTRIES.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {mode === 'register' && registerStage === 'confirm' && (
                <label>
                  Confirmation code
                  <input
                    type="text"
                    name="registerCode"
                    required
                    value={formState.registerCode}
                    onChange={handleChange}
                    placeholder="Enter the 6-digit code"
                  />
                </label>
              )}

              {mode === 'forgot' && forgotStage !== 'request' && (
                <label>
                  Verification code
                  <input
                    type="text"
                    name="forgotCode"
                    required
                    value={formState.forgotCode}
                    onChange={handleChange}
                    placeholder="Your reset code"
                  />
                </label>
              )}

              {mode === 'forgot' && forgotStage === 'reset' && (
                <>
                  <label>
                    New password
                    <input
                      type="password"
                      name="newPassword"
                      required
                      value={formState.newPassword}
                      onChange={handleChange}
                      placeholder="Create a new password"
                    />
                  </label>
                  <label>
                    Confirm new password
                    <input
                      type="password"
                      name="confirmNewPassword"
                      required
                      value={formState.confirmNewPassword}
                      onChange={handleChange}
                      placeholder="Repeat the new password"
                    />
                  </label>
                </>
              )}

              <button className="access-lab__submit" type="submit" disabled={isLoading}>
                {isLoading ? 'Contacting endpoint…' : submitLabel}
              </button>
            </form>
          </section>

          <section className="access-lab__panels">
            <article className="access-lab__panel access-lab__profile">
              <h3>Profile preview</h3>
              {session?.access ? (
                profile ? (
                  <div className="profile-card">
                    <div className="profile-card__header">
                      {profile.profile_picture ? (
                        <img src={profile.profile_picture} alt="Profile" />
                      ) : (
                        <div className="profile-card__avatar">{profileInitials}</div>
                      )}
                      <div>
                        <p className="profile-card__label">Token owner</p>
                        <h4>{profile.first_name || profile.username || 'Authenticated user'}</h4>
                        <p className="profile-card__sub">{profile.email || 'Email pending'}</p>
                      </div>
                    </div>
                    <div className="profile-card__grid">
                      {profileFields.map((field) => (
                        <div key={field.label} className="profile-card__field">
                          <span>{field.label}</span>
                          <strong>{field.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p>{profileLoading ? 'Loading profile details…' : 'Token stored. Press “Sync profile” to pull the latest data.'}</p>
                )
              ) : (
                <p>Request an access token to unlock the live profile card.</p>
              )}
            </article>

            <article className="access-lab__panel access-lab__console">
              <h3>Live console</h3>
              {lastExchange ? (
                <>
                  <div className="console-row">
                    <span className="console-method">{lastExchange.method}</span>
                    <code>{lastExchange.endpoint}</code>
                  </div>
                  <p className="console-status">
                    HTTP {lastExchange.statusCode} • {lastExchange.at}
                  </p>
                  {lastExchange.payload && (
                    <>
                      <p className="console-label">Payload</p>
                      <pre>{JSON.stringify(lastExchange.payload, null, 2)}</pre>
                    </>
                  )}
                  <p className="console-label">Response</p>
                  <pre>{JSON.stringify(lastExchange.body, null, 2)}</pre>
                </>
              ) : (
                <p>Fire one of the flows to watch the raw payload and response render in real time.</p>
              )}
            </article>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AccessLabPage;
